import { useAsyncData } from 'nuxt/app';
import { computed } from 'vue';
import type { ElectionCountdown } from '~/composables/useElectionCountdown';
import {
  buildElectionCountdown,
  ELECTION_DATE,
} from '~/composables/useElectionCountdown';
import { useGetData } from '~/composables/useGetData';
import type { LaravelApiResponse } from '~/types/plugins/api';

interface ApiElection {
  title: string | null;
  election_date: string | null;
  /** Days to the election counted by the API from its own clock. */
  days_left: number | null;
  status: ElectionCountdown['status'] | null;
}

const ELECTION_KEY = 'election-counter';

/**
 * Fetches the admin-configured election counter from the API and renders the
 * countdown from the `days_left`/`status` the API counted on its own clock —
 * nothing is ever counted locally. When the API has no figure, `countdown`
 * degrades to an inert `'past'` sentinel that `ElectionBanner` — the only
 * consumer — never shows (it hides while pending, on error, for a missing
 * `election_date`, and once the date has passed).
 *
 * `useAsyncData` rather than hand-rolled state, for three reasons that all
 * matter here:
 *
 * - It dedups on the key, so the two `ElectionBanner` mounts on the home page
 *   (the desktop `strip` and mobile `card` variants render simultaneously via
 *   responsive CSS, not conditionally) share one fetch and one result.
 * - It is request-scoped, so concurrent visitors cannot see each other's data.
 *   Module-scope refs were shared by the whole Node process once SSR was on.
 * - Nuxt awaits it before rendering and transfers the result in the payload, so
 *   the banner is present in the server HTML and hydrates without refetching.
 *   A fire-and-forget fetch writing into `useState` looked equivalent but was
 *   not: the payload captured `fetched: true` with the data still absent, and
 *   the client then skipped the request entirely and never rendered the banner.
 */
export function useElection() {
  const { data, error, status } = useAsyncData<ApiElection | null>(
    ELECTION_KEY,
    async () => {
      const { execute } = await useGetData<LaravelApiResponse<ApiElection>>();
      const result = await execute(ELECTION_KEY);

      return result?.data ?? null;
    },
    { default: () => null },
  );

  const pending = computed(() => status.value === 'pending');

  /**
   * The API's `days_left`/`status` are the ONLY source of the figure — no
   * clock is ever consulted here, on either side of the hydration boundary.
   * The device clock belongs to the user (a phone with its date set forward
   * showed a wrong "days left", VTW-569), and the local-counting fallback that
   * used to live here was anchored to a `server_time` the response cache made
   * up to five minutes stale — a clock-skew detector that was itself skewed.
   *
   * When the API has no figure (no election date configured), the sentinel
   * below is deliberately inert: `ElectionBanner` — the only consumer — hides
   * itself both for a missing `election_date` and for a `'past'` status, so
   * the zeros can never render. Server data or nothing.
   */
  const countdown = computed<ElectionCountdown>(() => {
    const election = data.value;

    if (
      election?.election_date &&
      typeof election.days_left === 'number' &&
      election.status
    ) {
      return buildElectionCountdown(
        election.days_left,
        election.status,
        election.election_date,
      );
    }

    return buildElectionCountdown(
      0,
      'past',
      election?.election_date ?? ELECTION_DATE,
    );
  });

  return {
    election: data,
    countdown,
    pending,
    error,
  };
}
